import React from 'react';
import { Checkbox, Input, palettes, IconButton } from '@indigo-ag/component-module';
import styled from 'styled-components';
import { ConditionalsValidationRow } from './ConditionalsTable.config';
import { CreditConditional } from '@indigo-ag/schema';
import { getConditionalText } from 'shared';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import i18next from 'i18next';

interface SelectedConditionalsListProps {
  items: ConditionalsValidationRow[];
  onChangeItem: (item: ConditionalsValidationRow) => void;
  onDeleteItem: (item: ConditionalsValidationRow) => void;
  alreadyAdded?: string[];
}

export interface ConditionalPayload {
  id: string | number;
  conditional: string | CreditConditional;
  observation: string;
  validated: boolean;
}

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 24rem;
  overflow-y: auto;
`;

const RowWrapper = styled.div<{ alreadyAdded?: boolean }>`
  display: grid;
  grid-template-columns: 2rem 0.8fr 1.2fr 2rem;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  color: ${palettes.brand.gray[800]};
  padding: 0.5rem 1rem;
  border: 1px solid ${({ alreadyAdded }) => (alreadyAdded ? palettes.brand.blue.dark : palettes.brand.gray[200])};
  border-radius: 0.3rem;
  box-shadow: 0 2px 8px 0 rgba(60, 60, 60, 0.06);
  transition: border-color 0.2s, box-shadow 0.2s;
`;

const RowInput = styled(Input)`
  width: 100%;
  height: 1.5rem;
  font-weight: 300;
  font-size: 0.875rem;
`;

const NameText = styled.span`
  min-width: 180px;
  font-size: 0.875rem;
`;

const EmptyList = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 10rem;
  width: 100%;
  font-size: 1.25rem;
  font-weight: bold;
  color: ${palettes.brand.gray[600]};
`;

const SelectedConditionalsList: React.FC<SelectedConditionalsListProps> = ({
  items,
  onChangeItem,
  onDeleteItem,
  alreadyAdded = []
}) => {
  if (items.length === 0) {
    return <EmptyList>{i18next.t('conditionalValidations-emptyList')}</EmptyList>;
  }

  return (
    <ListWrapper>
      {items.map(item => (
        <RowWrapper key={item.id} alreadyAdded={alreadyAdded.includes(item.conditional)}>
          <Checkbox checked={item.validated} onChange={e => onChangeItem({ ...item, validated: e.target.checked })} />
          <NameText>{getConditionalText(item.conditional)}</NameText>
          <RowInput
            label=""
            value={item.observation}
            onChange={e => onChangeItem({ ...item, observation: e.target.value })}
            placeholder={i18next.t('conditionalValidations-observationPlaceholder')}
            sizeVariant="slim"
          />
          <IconButton icon={faTrash} onClick={() => onDeleteItem(item)} colorVariant="destructive" />
        </RowWrapper>
      ))}
    </ListWrapper>
  );
};

export default SelectedConditionalsList;
